import * as yup from 'yup';
import {
  EstadoCivil,
  Genero,
  ICnaeItem,
  IDadosEmpresarioIndividual,
  IEnderecoEstruturado,
  IFilialDoAto,
  IRepresentantePJ,
  PorteEmpresarial,
  RegimeCasamento,
  TipoAlteracao,
  TipoOperacaoSocietario,
  TipoPessoaSocio,
  TipoVinculoSocio,
} from '../../../../hooks/useSocietario';
import { UF_NOME_SELECT_OPTIONS } from '../../../../constants/ufs';
import { dateRangeTest, isValidCNPJ, NIRE_REGEX } from '../../../../utils/documentValidation';
import { validarCpf } from '../../../../utils/validarCpf';
import { ORGAO_COM_UF_MAX, temOrgaoEUf } from './orgaoEmissor';
import {
  motivoDestinoInvalido,
  parNoVigente,
  recebidoPorOrdemDe,
  somaCedida,
} from './regrasDoPassoSocios';

// Orgao emissor com a UF de emissao (02/10): o contrato escreve "RG nº 351636 SSP/GO".
const MSG_ORGAO_COM_UF = 'Informe o órgão e a UF de emissão (ex.: SSP/GO)';
// Acima do limite o contrato nao gera (e acima de 30, tamanho da coluna, nem salva).
const MSG_ORGAO_TAMANHO = `Órgão com a UF: até ${ORGAO_COM_UF_MAX} caracteres (use a sigla, ex.: SSP/GO)`;

// Lookup precomputado pra .oneOf — nomes dos 27 estados.
const UF_NOMES = UF_NOME_SELECT_OPTIONS.map(o => o.value);

// Estado civil que **exige** regime de casamento + (idealmente) conjuge.
// Mesma lista que o backend valida em `_validar_socio_pf_regime`.
export const ESTADOS_QUE_EXIGEM_REGIME: EstadoCivil[] = ['casado', 'uniao_estavel'];

// Regimes em que o nome e o CPF do cônjuge são obrigatórios (pedido do societário, 05/10). São
// os dois do art. 977 do Código Civil.
export const REGIMES_QUE_EXIGEM_CONJUGE: RegimeCasamento[] = [
  'comunhao_universal_de_bens',
  'separacao_obrigatoria_de_bens',
];

// Transformação EI→LTDA (Fase 2): a Parte B é um corpo de CONSTITUIÇÃO, então o
// fluxo "constituinte" (qualif completa dos sócios, endereço estruturado,
// foro/capital/porte/cnaes, objeto via CNAEs) vale também pra transformação.
export const ehFluxoConstituinte = (op: TipoOperacaoSocietario): boolean =>
  op === 'constituicao' || op === 'transformacao';

// #3 (2026-06-11) Caminho B: em ALTERACAO a qualificacao do socio que
// CONTINUA (manter/alterar) vem do vigente preservado. Os campos de qualif
// (RG, orgao, estado civil, profissao, endereco) so sao OBRIGATORIOS pra
// socio NOVO (adicionar) ou constituicao — pro continuante ficam opcionais
// (editaveis + pre-preenchidos pela extracao, mas herdados do vigente se
// vazios). Usado nos `.when` de qualif do socioSchema.

// Qualif obrigatória APENAS pra sócio novo (adicionar) ou constituição — nesses
// casos o form é a ÚNICA fonte. Fase 1 (2026-07-16): o CONTINUANTE (alterar) não
// exige mais a qualif completa — o backend aplica o aspecto marcado CIRURGICAMENTE
// (nome/profissão/estado_civil dos campos diretos; endereço via campos
// estruturados) e PRESERVA o resto do vigente byte-fiel. Diretriz do user: qualif
// vem do vigente → nunca bloquear por dado faltante; campo vazio no continuante =
// noop no backend (preserva o vigente). `tipos` mantido na assinatura (yup passa
// os 4 valores observados); não é mais usado.
const exigeQualifCompleta = (
  tp: TipoPessoaSocio,
  acao: AcaoSocio | null,
  tipos: string[] | null | undefined,
  op: TipoOperacaoSocietario,
): boolean => tp === 'pf' && (ehFluxoConstituinte(op) || acao === 'adicionar');

// De quem o nome e o CPF do cônjuge são cobrados: sócio PF casado ou em união estável num dos
// dois regimes. Na abertura, de todos; na alteração, de quem entra e de quem continua com o
// aspecto "Estado civil" marcado (é alteração de dados do sócio: quem só continua fica como o
// vigente). Fora quem sai e o administrador não sócio, que não tem quotas. Usada pelo yup e pela
// tela.
export const exigeDadosDoConjuge = (
  socio:
    | Partial<
        Pick<
          ISocioFormValues,
          | 'tipo_pessoa'
          | 'tipo_vinculo'
          | 'estado_civil'
          | 'regime_casamento'
          | 'acao'
          | 'tipos_alteracao_socio'
        >
      >
    | null
    | undefined,
): boolean => {
  if (!socio || socio.tipo_pessoa !== 'pf') return false;
  if (socio.tipo_vinculo === 'administrador' || socio.acao === 'sair') return false;
  if (socio.acao === 'manter') return false;
  const marcouEstadoCivil = (socio.tipos_alteracao_socio ?? []).includes('estado_civil');
  if (socio.acao === 'alterar' && !marcouEstadoCivil) return false;
  if (!ESTADOS_QUE_EXIGEM_REGIME.includes(socio.estado_civil as EstadoCivil)) return false;
  return REGIMES_QUE_EXIGEM_CONJUGE.includes(socio.regime_casamento as RegimeCasamento);
};

// Casado ou em união estável: só destes o cadastro leva nome e CPF do cônjuge. Trocar o estado
// civil esconde o bloco na tela, mas o que foi digitado continua no formulário.
export const temConjuge = (estadoCivil: string | null | undefined): boolean =>
  ESTADOS_QUE_EXIGEM_REGIME.includes(estadoCivil as EstadoCivil);

const soDigitos = (valor: string | null | undefined): string => (valor ?? '').replace(/\D/g, '');

// As chaves do sócio de que a cobrança depende, na ordem em que o yup as entrega.
const CHAVES_DO_CONJUGE = [
  'tipo_pessoa',
  'tipo_vinculo',
  'estado_civil',
  'regime_casamento',
  'acao',
  'tipos_alteracao_socio',
];
const cobraConjuge = (
  tipoPessoa: TipoPessoaSocio,
  tipoVinculo: TipoVinculoSocio,
  estadoCivil: string | null,
  regime: string | null,
  acao: AcaoSocio | null,
  aspectos: TipoAlteracaoSocio[] | null,
): boolean =>
  exigeDadosDoConjuge({
    tipo_pessoa: tipoPessoa,
    tipo_vinculo: tipoVinculo,
    estado_civil: estadoCivil,
    regime_casamento: regime,
    acao,
    tipos_alteracao_socio: aspectos,
  });

// Opcoes para o select de estado civil. Backend valida via Literal.
export const ESTADO_CIVIL_OPTIONS: Array<{ value: EstadoCivil; label: string }> = [
  { value: 'solteiro', label: 'Solteiro(a)' },
  { value: 'casado', label: 'Casado(a)' },
  { value: 'divorciado', label: 'Divorciado(a)' },
  { value: 'viuvo', label: 'Viúvo(a)' },
  { value: 'uniao_estavel', label: 'União estável' },
  { value: 'separado_judicialmente', label: 'Separado(a) judicialmente' },
];

// Opcoes para o select de regime — Codigo Civil + estrangeiro_nao_aplicavel.
export const REGIME_CASAMENTO_OPTIONS: Array<{ value: RegimeCasamento; label: string }> = [
  { value: 'comunhao_parcial_de_bens', label: 'Comunhão parcial de bens' },
  { value: 'comunhao_universal_de_bens', label: 'Comunhão universal de bens' },
  { value: 'separacao_total_de_bens', label: 'Separação total de bens' },
  { value: 'separacao_obrigatoria_de_bens', label: 'Separação obrigatória (art. 1.641 CC)' },
  { value: 'participacao_final_nos_aquestos', label: 'Participação final nos aquestos' },
  { value: 'estrangeiro_nao_aplicavel', label: 'Estrangeiro — não aplicável' },
];

// Tipos de alteracao com label legivel + descricao para o multi-select.
// `oculto` (2026-06-18): tipo nao ofertado no wizard (decisao "remover Outras
// por enquanto" — v3 cobre o resto deterministicamente). MANTIDO na lista pra
// nao quebrar a validacao yup `oneOf` de contratos salvos que ja tem o valor;
// o TiposAlteracaoStep filtra `oculto` na renderizacao.
export const TIPO_ALTERACAO_OPTIONS: Array<{
  value: TipoAlteracao;
  label: string;
  descricao: string;
  oculto?: boolean;
}> = [
  {
    value: 'quadro_societario',
    label: 'Quadro societário',
    descricao: 'Entrada/saída/transferência de quotas entre sócios',
  },
  {
    value: 'dados_socio',
    label: 'Dados de sócio',
    descricao: 'Profissão, estado civil, nome ou endereço de quem continua na sociedade',
  },
  {
    value: 'capital',
    label: 'Capital social',
    descricao: 'Aumento ou redução do valor do capital social',
  },
  {
    value: 'endereco',
    label: 'Endereço da sede',
    descricao: 'Mudança do endereço da sede da sociedade',
  },
  {
    value: 'abertura_filial',
    label: 'Abertura de filial',
    descricao: 'Abertura de uma ou mais filiais (endereços)',
  },
  {
    value: 'extincao_filial',
    label: 'Extinção de filial',
    descricao: 'Encerramento de uma ou mais filiais (endereço, NIRE e CNPJ)',
  },
  {
    value: 'alteracao_filial',
    label: 'Alteração de filial',
    descricao:
      'Filial que muda de endereço, de atividades, de capital destacado ou de nome fantasia',
  },
  {
    value: 'razao_social',
    label: 'Razão social',
    descricao: 'Alteração do nome empresarial',
  },
  // Posicao NAO e' cosmetica: o grid e' de 2 colunas preenchido da esquerda pra
  // direita, entao aqui a fantasia cai logo ABAIXO da razao social — os dois
  // atos parentes ficam adjacentes na tela.
  {
    value: 'nome_fantasia',
    label: 'Nome fantasia',
    // Sem "supressão": o wizard ainda não suporta suprimir fantasia (campo
    // vazio não gera patch — ver tratarNomeFantasia). Prometer no checkbox o
    // que a geração recusa confunde o operador.
    descricao: 'Adoção ou alteração do nome fantasia',
  },
  {
    value: 'objeto_social',
    label: 'Objeto social (CNAEs)',
    descricao: 'Alteração das atividades exercidas pela sociedade',
  },
  {
    value: 'administracao',
    label: 'Administração',
    descricao: 'Alteração dos administradores ou regras de administração',
    // Wave 2 (2026-06-19): tipo REATIVADO — administração agora roteia pro v3
    // determinístico (mudança de admin via aspecto Vínculo / sair+permanece).
    // Os fraseados que o v3 ainda não reescreve caem no V_VINCULO_ADMIN.
  },
  {
    value: 'clausulas',
    label: 'Outras cláusulas',
    descricao:
      'Alterar ou incluir cláusula do contrato vigente, pela conversa com a IA, e textos prontos (distribuição de lucros, regência supletiva)',
  },
  {
    value: 'natureza_juridica',
    label: 'Natureza jurídica',
    descricao: 'Troca do tipo societário (ex.: EI/MEI → LTDA, LTDA → SLU)',
    // Oculto no wizard DE PROPOSITO: quem gera o documento da troca de natureza e
    // a operacao `transformacao` (EI/MEI -> LTDA), com steps proprios. Aqui o tipo
    // existe pro card da legalizacao marcar o processo e derivar a rota; ofertar
    // no multi-select prometeria uma geracao que nao acontece.
    oculto: true,
  },
  {
    value: 'outras',
    label: 'Outras',
    descricao: 'Outras alterações descritas no campo de instruções livres',
    oculto: true,
  },
];

/** Sessao 2026-05-11 (socios sync). */
export type AcaoSocio = 'manter' | 'sair' | 'alterar' | 'adicionar';

/** Sessao 2026-05-11 (item 15 Fase 4) — sub-aspectos do `acao=alterar`. */
export type TipoAlteracaoSocio =
  | 'quotas'
  | 'endereco'
  | 'nome'
  | 'profissao'
  | 'vinculo'
  | 'estado_civil'
  | 'representante';

/** Aspectos de qualificação: o que "Dados de sócio" muda em quem continua. */
export const ASPECTOS_DE_QUALIFICACAO: TipoAlteracaoSocio[] = [
  'endereco',
  'nome',
  'profissao',
  'estado_civil',
  'representante',
];

export interface IDestinoQuotaForm {
  // Aceita null como placeholder "ainda nao selecionado" — RHF popula campos
  // vazios ao entrar em acao=sair. Validacao yup exige valor antes do submit.
  socio_ordem: number | null;
  qtd: number | null;
}

export interface ISocioFormValues {
  tipo_vinculo: TipoVinculoSocio;
  tipo_pessoa: TipoPessoaSocio;
  // SCP (Fase 3) — papel ostensiva/participante (eixo próprio da SCP; NULL fora dela).
  papel_scp?: 'ostensiva' | 'participante' | null;
  nome: string;
  // PF
  cpf?: string | null;
  rg?: string | null;
  rg_orgao_emissor?: string | null;
  // Schema-extension v3 M2a — RG via.
  rg_via?: string | null;
  nacionalidade?: string | null;
  estado_civil?: EstadoCivil | string | null;
  // Fase 4 Slice E — regime + conjuge.
  regime_casamento?: RegimeCasamento | string | null;
  pacto_antenupcial?: boolean | null;
  nome_conjuge?: string | null;
  cpf_conjuge?: string | null;
  profissao?: string | null;
  dt_nascimento?: string | null;
  filiacao_pai?: string | null;
  filiacao_mae?: string | null;
  naturalidade?: string | null;
  // Schema-extension v3 M1 — gênero PF.
  genero?: Genero | null;
  // PJ
  cnpj?: string | null;
  // Schema-extension v3 M2 — PJ sócia (registro próprio).
  junta_uf?: string | null;
  nire?: string | null;
  representante_nome?: string | null;
  representante_cpf?: string | null;
  // Schema-extension v3 elo 3 — sub-objetos ergonômicos.
  // Backend desempacota em campos flat no banco. EnderecoForm escreve em
  // `endereco_estruturado`; sub-ficha PJ escreve em `representante`.
  endereco_estruturado?: IEnderecoEstruturado | null;
  representante?: IRepresentantePJ | null;
  // Contato
  endereco?: string | null;
  email?: string | null;
  telefone?: string | null;
  quotas?: number | null;
  // Sessao 2026-05-11 (socios sync). Null em constituicao.
  acao?: AcaoSocio | null;
  redistribuicao_quotas?: IDestinoQuotaForm[] | null;
  // Sessao 2026-05-11 (item 15 Fase 4)
  tipos_alteracao_socio?: TipoAlteracaoSocio[] | null;
  // Porta 2 Wave 1 (2026-06-18): socio-administrador que SAI mas PERMANECE como
  // administrador nao-socio. So aparece/aplica em acao=sair de quem e/era admin.
  permanece_como_administrador?: boolean | null;
}

// SCP (Fase 3) — config específico da natureza scp (espelha `DadosScp` do backend,
// persistido no JSONB `dados_scp`). O núcleo (nome/objeto/capital/sócios/foro)
// reusa os campos do wizard; aqui só o SCP-específico (modos + flags de cláusula).
export interface IDadosScp {
  objeto_modo?: 'cnae' | 'livre' | null;
  diretrizes_objeto?: string[] | null;
  capital_nominal?: boolean | null;
  prazo_tipo?: 'determinado' | 'indeterminado' | null;
  prazo_duracao_valor?: number | null;
  prazo_duracao_unidade?: 'dias' | 'meses' | 'anos' | null;
  prazo_condicao_termino?: string | null;
  distribuicao_desproporcional?: boolean | null;
  distribuicao_prazo_pagamento?: string | null;
  limite_alcada?: boolean | null;
  limite_alcada_valor?: number | null;
  obrigacoes_detalhadas?: boolean | null;
  deliberacoes_quorum?: string | null;
  pro_labore?: boolean | null;
  acordo_socios?: boolean | null;
  admissao_livre?: boolean | null;
  exclusao_retirada?: boolean | null;
  confidencialidade?: boolean | null;
  confidencialidade_multa?: number | null;
  mediacao_previa?: boolean | null;
  mediacao_camara?: string | null;
  assinatura_detalhada?: boolean | null;
  testemunhas?: boolean | null;
}

/** Atividades, capital destacado e nome fantasia da filial: facultativos (DREI, item 4.12.2). */
export interface IDadosDaFilial {
  atividades?: string | null;
  /** Número, ou '' enquanto o campo está em branco (é o que o campo de moeda grava). */
  capital_destacado?: number | string | null;
  nome_fantasia?: string | null;
}

/** Filial que o ato encerra: endereço (7 campos), NIRE e CNPJ dela. */
export type IFilialAFechar = Omit<IFilialDoAto, 'acao' | 'endereco_novo' | keyof IDadosDaFilial>;

/** Filial que o ato abre: o endereço e os dados facultativos. */
export type IFilialAberta = IEnderecoEstruturado & IDadosDaFilial;

/**
 * Filial alterada: CNPJ, NIRE (opcional) e o endereço de hoje; o que muda é o endereço (`novo`,
 * com `muda_endereco`), as atividades, o capital destacado ou o nome fantasia.
 */
export type IFilialAAlterar = IFilialAFechar &
  IDadosDaFilial & { novo?: IEnderecoEstruturado | null; muda_endereco?: boolean | null };

export interface IWizardFormValues {
  tipo_operacao: TipoOperacaoSocietario;
  template_id?: number | null;
  contrato_pai_id?: number | null;
  empresa_id?: number | null;
  // Empresa
  razao_social?: string | null;
  nome_fantasia?: string | null;
  cnpj?: string | null;
  capital_social?: number | null;
  objeto_social?: string | null;
  cnaes: ICnaeItem[];
  endereco?: string | null;
  foro?: string | null;
  finalidade_livre?: string | null;
  // Registro
  data_inicio_atividades?: string | null;
  cidade_assinatura?: string | null;
  junta_comercial_estado?: string | null;
  // Alteracao
  junta_comercial_sigla?: string | null;
  nire?: string | null;
  data_constituicao?: string | null;
  numero_alteracao?: number | null;
  razao_social_consolidada?: string | null;
  // Fase 6 Slice C — multi-select de tipos de alteracao. Vazio em constituicao.
  tipos_alteracao?: TipoAlteracao[];
  // Filiais abertas pelo ato (tipo `abertura_filial`): o endereço e os dados facultativos.
  filiais?: IFilialAberta[];
  // Filiais encerradas pelo ato (tipo `extincao_filial`): endereço, NIRE e CNPJ de cada uma.
  filiais_fechar?: IFilialAFechar[];
  // Filiais alteradas pelo ato (tipo `alteracao_filial`).
  filiais_alterar?: IFilialAAlterar[];
  // Pos-Fase 6: ID do contrato criado implicitamente pelo upload do contrato
  // vigente (endpoint `iniciar-alteracao-com-vigente`). Populado quando o
  // usuario faz upload no step `contrato_vigente`. Em alteracao e obrigatorio
  // (validado por `stepContratoVigenteSchema`); em constituicao fica null.
  contrato_id_inicial?: number | null;
  // Schema-extension v3 M3 — empresa estruturada (constituicao).
  // Foro/capital/porte/data_assinatura standalone; endereco_estruturado sub-objeto.
  // EnderecoForm escreve em endereco_estruturado; backend desempacota nos
  // 7 endereco_* da `tb_contrato_societario` (M3).
  foro_cidade?: string | null;
  foro_uf?: string | null;
  capital_qtd_quotas?: number | null;
  capital_valor_quota?: number | null;
  porte?: PorteEmpresarial | null;
  natureza_societaria?: 'ltda' | 'holding' | 'scp' | null;
  /** Constituição: textos prontos marcados (chaves do back, 15/10). */
  textos_prontos?: string[] | null;
  data_assinatura?: string | null;
  endereco_estruturado?: IEnderecoEstruturado | null;
  // Transformação EI→LTDA — snapshot do EI de origem (só nessa operação).
  dados_ei?: IDadosEmpresarioIndividual | null;
  // SCP — config da natureza scp (só nessa natureza).
  dados_scp?: IDadosScp | null;
  // ── Fase 0 (legalização) — UI-only, NÃO entram no buildCreatePayload ──
  // Gate "cadastro bate com o último contrato?" (2ª trava do guia da junta).
  // Só em alteração; validado em `stepTiposAlteracaoSchema`. Persiste na Fase 1.
  cadastro_confere?: boolean;
  // Briefing expandido (só constituição) — coletado/exibido, ainda NÃO vai pro
  // contrato nem persiste (Fase 1 dá coluna + migration + entrada na whitelist).
  imovel_tipo?: 'proprio' | 'alugado' | null;
  imovel_area_total?: number | null;
  imovel_area_ocupada?: number | null;
  imovel_inscricao_iptu?: string | null;
  imovel_zona?: 'urbana' | 'rural' | null;
  regime_tributario_pretendido?: 'simples' | 'presumido' | 'real' | null;
  atividade_no_endereco?: boolean | null;
  socios: ISocioFormValues[];
}

/**
 * Regra do ">=1 administrador" — compartilhada pelo botao Gerar (`index.tsx`)
 * e pelo aviso do passo Socios (`StepSocios`). Mora aqui porque os dois
 * consomem, e uma copia byte-identica em cada um ja quase divergiu (review do
 * PR #626, 2026-08-07).
 *
 * Exclui `acao='sair'` — admin que esta saindo nao conta como remanescente —
 * EXCETO quem sai mas PERMANECE administrando (Porta 2 Wave 1, 2026-06-18),
 * que segue satisfazendo a regra.
 */
export const temAdministradorEm = (
  socios:
    | Pick<ISocioFormValues, 'tipo_vinculo' | 'acao' | 'permanece_como_administrador'>[]
    | undefined
    | null,
): boolean =>
  (socios ?? []).some(
    s =>
      (s?.tipo_vinculo === 'socio_administrador' || s?.tipo_vinculo === 'administrador') &&
      (s?.acao !== 'sair' || !!s?.permanece_como_administrador),
  );

export const socioDefaults: ISocioFormValues = {
  tipo_vinculo: 'socio',
  tipo_pessoa: 'pf',
  papel_scp: null,
  nome: '',
  cpf: '',
  rg: '',
  rg_orgao_emissor: '',
  rg_via: null,
  // Default cravado 2026-05-27 — nacionalidade automatica como
  // "brasileiro" (forma M, backend flexiona pra F via filtro `concordar`).
  // Campo nao e mais exposto na UI (decisao societario: 99% dos casos).
  nacionalidade: 'brasileiro',
  estado_civil: '',
  regime_casamento: '',
  pacto_antenupcial: null,
  nome_conjuge: '',
  cpf_conjuge: '',
  profissao: '',
  dt_nascimento: '',
  filiacao_pai: '',
  filiacao_mae: '',
  naturalidade: '',
  genero: null,
  cnpj: '',
  junta_uf: null,
  nire: null,
  representante_nome: '',
  representante_cpf: '',
  // Schema-extension v3 elo 3 — defaults com OBJETOS VAZIOS (não null)
  // pra que o RHF consiga escrever em paths nested (ex:
  // `socios.0.endereco_estruturado.logradouro`). Com parent=null o
  // setValue ignora silenciosamente em alguns cenários do RHF v7. Vimos
  // empiricamente 2026-05-26: empresa (top-level) escrevia OK, sócio
  // (nested) ficava null.
  endereco_estruturado: {
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: null,
    cep: '',
  },
  // Mesma motivacao do endereco_estruturado: paths nested
  // (`socios.${i}.representante.endereco_estruturado.logradouro`) precisam
  // dos parents nao-null pra que o RHF v7 escreva via setValue. Aqui o
  // path tem 2 niveis (representante + endereco_estruturado) — ambos
  // inicializados como objeto.
  representante: {
    nome: '',
    cpf: '',
    rg: '',
    rg_orgao: '',
    rg_via: '',
    nacionalidade: 'brasileiro',
    estado_civil: null,
    regime_casamento: null,
    dt_nascimento: '',
    profissao: '',
    genero: null,
    endereco_estruturado: {
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      uf: null,
      cep: '',
    },
  },
  endereco: '',
  email: '',
  telefone: '',
  quotas: null,
  acao: null,
  redistribuicao_quotas: null,
  tipos_alteracao_socio: null,
  permanece_como_administrador: false,
};

export const wizardDefaults: IWizardFormValues = {
  tipo_operacao: 'constituicao',
  template_id: null,
  contrato_pai_id: null,
  empresa_id: null,
  razao_social: '',
  nome_fantasia: '',
  cnpj: '',
  capital_social: null,
  objeto_social: '',
  cnaes: [],
  endereco: '',
  foro: '',
  finalidade_livre: '',
  data_inicio_atividades: '',
  cidade_assinatura: '',
  junta_comercial_estado: '',
  junta_comercial_sigla: '',
  nire: '',
  data_constituicao: '',
  numero_alteracao: null,
  razao_social_consolidada: '',
  tipos_alteracao: [],
  filiais: [],
  filiais_fechar: [],
  filiais_alterar: [],
  contrato_id_inicial: null,
  // Schema-extension v3 elo 3 — defaults pra constituicao v3.
  foro_cidade: null,
  foro_uf: null,
  capital_qtd_quotas: null,
  capital_valor_quota: null,
  porte: null,
  natureza_societaria: 'ltda',
  data_assinatura: null,
  // Paridade defensiva com socioDefaults.endereco_estruturado: inicializa
  // como objeto pra evitar dependencia de comportamento RHF v7 por path-depth.
  endereco_estruturado: {
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: null,
    cep: '',
  },
  // Transformação EI→LTDA — defaults nested (NUNCA null: RHF v7 só escreve em
  // path nested se o pai for objeto). Os 2 endereços também objetos.
  dados_ei: {
    denominacao: '',
    nome: '',
    cpf: '',
    rg: '',
    rg_orgao: '',
    rg_via: '',
    nacionalidade: 'brasileiro',
    estado_civil: null,
    regime_casamento: null,
    dt_nascimento: '',
    naturalidade: '',
    profissao: '',
    genero: null,
    endereco_estruturado: {
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      uf: null,
      cep: '',
    },
    sede_estruturada: {
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      uf: null,
      cep: '',
    },
  },
  // SCP — defaults do config. objeto livre + capital nominal + prazo determinado
  // são o caso dominante (projeto/evento); flags começam off (operador liga).
  dados_scp: {
    objeto_modo: 'livre',
    diretrizes_objeto: [],
    capital_nominal: true,
    prazo_tipo: 'determinado',
    prazo_duracao_valor: null,
    prazo_duracao_unidade: 'meses',
    prazo_condicao_termino: '',
    distribuicao_desproporcional: false,
    distribuicao_prazo_pagamento: '',
    limite_alcada: false,
    limite_alcada_valor: null,
    obrigacoes_detalhadas: false,
    deliberacoes_quorum: '',
    pro_labore: false,
    acordo_socios: true,
    admissao_livre: false,
    exclusao_retirada: false,
    confidencialidade: false,
    confidencialidade_multa: null,
    mediacao_previa: false,
    mediacao_camara: '',
    assinatura_detalhada: false,
    testemunhas: false,
  },
  // Fase 0 (legalização) — UI-only (não vão pro payload).
  cadastro_confere: false,
  imovel_tipo: null,
  imovel_area_total: null,
  imovel_area_ocupada: null,
  imovel_inscricao_iptu: null,
  imovel_zona: null,
  regime_tributario_pretendido: null,
  atividade_no_endereco: null,
  socios: [{ ...socioDefaults }],
};

// Pos-Fase 6: schema do step novo "Contrato vigente". So existe em alteracao.
// Valida que o upload foi feito (`contrato_id_inicial > 0`). O componente
// `ContratoVigenteStep` seta esse campo via `setValue` apos o POST do endpoint.
export const stepContratoVigenteSchema = yup.object().shape({
  tipo_operacao: yup
    .mixed<TipoOperacaoSocietario>()
    .oneOf(['constituicao', 'alteracao', 'transformacao']),
  contrato_id_inicial: yup
    .number()
    .nullable()
    .when('tipo_operacao', {
      is: 'alteracao',
      then: s =>
        s
          .min(1, 'Faça upload do contrato vigente para continuar')
          .required('Faça upload do contrato vigente para continuar'),
      otherwise: s => s.nullable(),
    }),
});

// Fase 6 Slice C — schema do step novo "Tipos de alteracao".
// Backend exige >=1 item. Sem dedup necessario aqui (multi-select garante).
export const stepTiposAlteracaoSchema = yup.object().shape({
  tipo_operacao: yup
    .mixed<TipoOperacaoSocietario>()
    .oneOf(['constituicao', 'alteracao', 'transformacao']),
  tipos_alteracao: yup
    .array()
    .of(yup.mixed<TipoAlteracao>().oneOf(TIPO_ALTERACAO_OPTIONS.map(o => o.value)))
    .when('tipo_operacao', {
      is: 'alteracao',
      then: s => s.min(1, 'Marque ao menos 1 tipo de alteração').required(),
      otherwise: s => s.notRequired(),
    }),
  // Fase 0 (legalização) — gate "cadastro bate com o último contrato?" (2ª trava
  // do guia da junta). Obrigatório SÓ quando o wizard foi aberto pela Legalização
  // (`$ehFluxoLegalizacao` no context); em alteração AVULSA (módulo Contratos) não
  // trava. UI-only (não vai pro payload) — `validateStep('tipos_alteracao')` roda isto.
  cadastro_confere: yup.boolean().when('$ehFluxoLegalizacao', {
    is: true,
    then: s =>
      s.when('tipo_operacao', {
        is: 'alteracao',
        then: s2 => s2.oneOf([true], 'Confirme que o cadastro na junta bate com o último contrato'),
        otherwise: s2 => s2.notRequired(),
      }),
    otherwise: s => s.notRequired(),
  }),
});

// Schemas por step — validados manualmente na navegação.

// Endereço estruturado com campos críticos obrigatórios (reusado nos 2
// endereços do EI: residência + sede original).
// Sede da empresa. Na alteração o número pode faltar: endereço de quadra e lote não
// tem, e o contrato sai sem ele (regra do motor desde 24/09).
const camposDaSede = (numeroObrigatorio: boolean) => ({
  logradouro: yup.string().required('Logradouro é obrigatório'),
  numero: numeroObrigatorio
    ? yup.string().required('Número é obrigatório (use "s/n" se aplicável)')
    : yup.string().nullable(),
  complemento: yup.string().nullable(),
  bairro: yup.string().required('Bairro é obrigatório'),
  cidade: yup.string().required('Cidade é obrigatória'),
  uf: yup
    .string()
    .matches(/^[A-Z]{2}$/, 'UF deve ter 2 letras')
    .required('UF é obrigatória'),
  cep: yup
    .string()
    .matches(/^\d{5}-?\d{3}$/, 'CEP inválido (formato 00000-000)')
    .required('CEP é obrigatório'),
});

// Filial encerrada: o que a cláusula cita e a Junta cobra (regra de 24/09). O back confere de novo.
const filialAFechar = () =>
  yup.object().shape({
    ...camposDaSede(false),
    nire: yup
      .string()
      .nullable()
      .required('NIRE da filial é obrigatório')
      .test(
        'nire-11',
        'O NIRE da filial tem 11 dígitos',
        v => !v || v.replace(/\D/g, '').length === 11,
      ),
    cnpj: yup
      .string()
      .nullable()
      .required('CNPJ da filial é obrigatório')
      .test('cnpj-valido', 'CNPJ inválido', v => !v || isValidCNPJ(v)),
  });

// Dados facultativos da filial (DREI, item 4.12.2), na abertura e na alteração.
const dadosDaFilial = () => ({
  atividades: yup.string().nullable(),
  nome_fantasia: yup.string().nullable(),
  capital_destacado: yup
    .number()
    .nullable()
    .transform((valor, original) => (original === '' || original == null ? null : valor))
    .typeError('Informe um valor em reais')
    .moreThan(0, 'O capital destacado tem de ser maior que zero'),
});

// DREI, item 4.12.2: a soma dos destaques de capital das filiais é MENOR que o capital da
// sociedade. O back recusa o ato; aqui o operador vê no campo. Soma os dois tipos do ato.
function destaquesCabemNoCapital(this: any, lista: any) {
  const pai = this.parent ?? {};
  const tipos: string[] = pai.tipos_alteracao ?? [];
  const capital = Number(pai.capital_social) || 0;
  const destaque = (f: any) => Number(f?.capital_destacado) || 0;
  const soma = (filiais: any[] | null | undefined) =>
    (filiais ?? []).reduce((total: number, f: any) => total + destaque(f), 0);
  const total =
    (tipos.includes('abertura_filial') ? soma(pai.filiais) : 0) +
    (tipos.includes('alteracao_filial') ? soma(pai.filiais_alterar) : 0);
  const i = (lista ?? []).findIndex((f: any) => destaque(f) > 0);
  // Em centavos: 1000,01 + 2001,60 dava 3001,6099999999997 e passava por menor que 3001,61.
  const cabe = Math.round(total * 100) < Math.round(capital * 100);
  return capital <= 0 || cabe || i < 0
    ? true
    : this.createError({
        path: `${this.path}[${i}].capital_destacado`,
        message:
          'A soma dos destaques de capital das filiais tem de ser menor que o capital social',
      });
}

// Filial alterada: o CNPJ a identifica (o DREI pede em alteração de filial) e o NIRE é
// opcional; o endereço de hoje é o da Junta. Muda o endereço (o novo vai completo), as
// atividades, o capital destacado ou o nome fantasia — pelo menos um. O back confere de novo.
const filialAAlterar = () =>
  yup
    .object()
    .shape({
      ...camposDaSede(false),
      // Endereço de hoje: é o da Junta, e ela pode não ter bairro nem CEP. Exigir obrigaria a
      // inventar dado (o back só pede logradouro e cidade).
      bairro: yup.string().nullable(),
      cep: yup
        .string()
        .nullable()
        .test('cep-formato', 'CEP inválido (00000-000)', v => !v || /^\d{5}-?\d{3}$/.test(v)),
      nire: yup
        .string()
        .nullable()
        .test(
          'nire-11',
          'O NIRE da filial tem 11 dígitos',
          v => !v || v.replace(/\D/g, '').length === 11,
        ),
      cnpj: yup
        .string()
        .nullable()
        .required('CNPJ da filial é obrigatório')
        .test('cnpj-valido', 'CNPJ inválido', v => !v || isValidCNPJ(v)),
      muda_endereco: yup.boolean().nullable(),
      novo: yup
        .object()
        .nullable()
        .when('muda_endereco', {
          is: (muda: boolean | null | undefined) => muda !== false,
          then: s => s.shape(camposDaSede(false)).required('Preencha o endereço novo da filial'),
        }),
      ...dadosDaFilial(),
    })
    // Sem endereço novo, a filial tem de mudar de atividades, de capital destacado ou de nome.
    .test('alguma-mudanca', '', function algumaMudanca(f) {
      const x = f as any;
      const muda =
        x?.muda_endereco !== false ||
        String(x?.atividades ?? '').trim() ||
        String(x?.nome_fantasia ?? '').trim() ||
        Number(x?.capital_destacado) > 0;
      return muda
        ? true
        : this.createError({
            path: `${this.path}.atividades`,
            message:
              'Informe o que muda nesta filial: endereço, atividades, capital destacado ou nome fantasia',
          });
    })
    // O back recusa o ato (endereço novo igual ao de hoje): aqui o operador vê no campo.
    .test('endereco-mudou', '', function enderecoMudou(f) {
      if ((f as any)?.muda_endereco === false) return true;
      const igual = (campo: keyof IEnderecoEstruturado) => {
        const limpo = (x: unknown) =>
          String(x ?? '')
            .trim()
            .toLowerCase();
        const [a, b] = [(f as any)?.[campo], (f as any)?.novo?.[campo]];
        return campo === 'cep'
          ? limpo(a).replace(/\D/g, '') === limpo(b).replace(/\D/g, '')
          : limpo(a) === limpo(b);
      };
      const campos: Array<keyof IEnderecoEstruturado> = [
        'logradouro',
        'numero',
        'complemento',
        'bairro',
        'cidade',
        'uf',
        'cep',
      ];
      return String((f as any)?.novo?.logradouro ?? '').trim() && campos.every(igual)
        ? this.createError({
            path: `${this.path}.novo.logradouro`,
            message: 'O endereço novo é igual ao de hoje',
          })
        : true;
    });

const enderecoEstruturadoObrigatorio = () =>
  yup
    .object()
    .nullable()
    .shape({
      logradouro: yup.string().nullable().required('Logradouro é obrigatório'),
      numero: yup.string().nullable().required('Número é obrigatório'),
      complemento: yup.string().nullable(),
      bairro: yup.string().nullable().required('Bairro é obrigatório'),
      cidade: yup.string().nullable().required('Cidade é obrigatória'),
      uf: yup
        .string()
        .nullable()
        .required('UF é obrigatória')
        .test('uf-2-letras', 'UF deve ter 2 letras', v => !v || /^[A-Z]{2}$/.test(v)),
      cep: yup
        .string()
        .nullable()
        .required('CEP é obrigatório')
        .test('cep-formato', 'CEP inválido (00000-000)', v => !v || /^\d{5}-?\d{3}$/.test(v)),
    })
    .required('Preencha o endereço');

// Transformação EI→LTDA (Fase 2) — passo do Empresário Individual de origem.
// `cnpj`/`nire` são herdados pela LTDA (sub-rogação) e vivem no nível do
// contrato; `dados_ei` é o snapshot EI-exclusivo (JSONB).
export const stepEmpresarioIndividualSchema = yup.object().shape({
  cnpj: yup
    .string()
    .nullable()
    .required('CNPJ do Empresário Individual é obrigatório')
    .test('cnpj-valido', 'CNPJ inválido', v => (!v ? true : isValidCNPJ(v))),
  nire: yup
    .string()
    .nullable()
    .test('nire-formato', 'NIRE inválido', v => !v || NIRE_REGEX.test(v)),
  dados_ei: yup
    .object()
    .nullable()
    .shape({
      denominacao: yup.string().nullable().required('Denominação do EI é obrigatória'),
      nome: yup.string().nullable().required('Nome do empresário é obrigatório'),
      cpf: yup
        .string()
        .nullable()
        .required('CPF do empresário é obrigatório')
        .test('cpf-valido', 'CPF inválido', v => (!v ? true : validarCpf(v))),
      rg: yup.string().nullable().required('RG é obrigatório'),
      rg_orgao: yup
        .string()
        .nullable()
        .required('Órgão emissor do RG é obrigatório')
        .test('orgao-com-uf', MSG_ORGAO_COM_UF, v => !v || temOrgaoEUf(v)),
      rg_via: yup.string().nullable(),
      nacionalidade: yup.string().nullable().required('Nacionalidade é obrigatória'),
      estado_civil: yup.string().nullable().required('Estado civil é obrigatório'),
      regime_casamento: yup
        .string()
        .nullable()
        .when('estado_civil', {
          is: (v: string | null) => ESTADOS_QUE_EXIGEM_REGIME.includes(v as EstadoCivil),
          then: r => r.required('Regime de bens é obrigatório quando casado / união estável'),
          otherwise: r => r.nullable(),
        }),
      dt_nascimento: yup.string().nullable().required('Data de nascimento é obrigatória'),
      naturalidade: yup.string().nullable(),
      profissao: yup.string().nullable().required('Profissão é obrigatória'),
      genero: yup
        .mixed<Genero>()
        .oneOf(['M', 'F'] as const)
        .nullable()
        .required('Selecione o gênero'),
      endereco_estruturado: enderecoEstruturadoObrigatorio(),
      sede_estruturada: enderecoEstruturadoObrigatorio(),
    })
    .required('Preencha os dados do Empresário Individual'),
});

export const step1Schema = yup.object().shape({
  tipo_operacao: yup
    .mixed<TipoOperacaoSocietario>()
    .oneOf(['constituicao', 'alteracao', 'transformacao'])
    .required('Selecione o tipo de operação'),
  // template_id opcional (opção C: backend auto-resolve o template ativo por tipo)
  template_id: yup.number().nullable(),
  // Pos-Fase 6: `contrato_pai_id` deixou de ser obrigatorio em alteracao —
  // a fonte de verdade do contrato anterior agora e o upload do arquivo
  // (step `contrato_vigente`). O campo continua existindo no schema pra
  // referencia logica opcional a uma alteracao anterior persistida no
  // sistema, mas nao bloqueia o avanco.
  contrato_pai_id: yup.number().nullable(),
});

// Regra juridica: a qualificacao completa do socio e obrigatoria no contrato
// social (Lei 10.406/2002 e IN DREI 81/2020). Sem esses campos, a IA tenta
// gerar qualificacoes com multiplos [A DEFINIR] que causam UX ruim no form
// de campos pendentes — evitamos isso exigindo-os aqui.
export const socioSchema = yup.object().shape({
  tipo_vinculo: yup
    .mixed<TipoVinculoSocio>()
    .oneOf(['socio', 'socio_administrador', 'administrador'])
    .required(),
  tipo_pessoa: yup.mixed<TipoPessoaSocio>().oneOf(['pf', 'pj']).required(),
  nome: yup.string().required('Nome / Razão social é obrigatório'),
  // Endereco legacy (string livre v1/v2). Em constituicao v3, o
  // EnderecoForm escreve em `endereco_estruturado` (validado abaixo) e
  // o legacy fica vazio — required quebra o wizard ("Endereço é obrigatório"
  // no step Revisão mesmo com o estruturado preenchido). Required somente
  // em alteracao p/ sócio que CONTINUA (v1/v2 consomem a string livre); o
  // sócio admitido usa o endereco_estruturado (igual constituicao) — exigir
  // o textarea dele quebraria o step, já que o campo nem é renderizado.
  // Caminho B (#3, 2026-06-11): em alteracao v3 o continuante herda o
  // endereco do vigente preservado — textarea OPCIONAL (editavel, mas nao
  // bloqueia). O socio admitido usa `endereco_estruturado` (validado
  // abaixo), nao este textarea. O override de endereco do continuante (o
  // operador marcar "endereco" e o backend aplicar) e o follow-up #4.
  endereco: yup.string().nullable(),
  // PF obrigatorios
  cpf: yup
    .string()
    .nullable()
    // DV (modulo 11) alem da presenca — espelha o cnpj. Sem isto, CPF
    // digitado a mao (sem upload de documento) passava o Continuar com
    // typo. validarComoCpf do CampoExtraidoWarning so dispara apos extracao.
    .test('cpf-valido', 'CPF inválido (dígito verificador não confere)', value =>
      !value ? true : validarCpf(value),
    )
    .when('tipo_pessoa', {
      is: 'pf',
      then: s => s.required('CPF é obrigatório'),
      otherwise: s => s.nullable(),
    }),
  rg: yup
    .string()
    .nullable()
    // Caminho B: obrigatorio pra socio novo/constituicao OU continuante que
    // marcou aspecto de qualif (backend regenera). Senao herda do vigente
    // (#3 + override 2026-06-11).
    .when(['tipo_pessoa', 'acao', 'tipos_alteracao_socio', '$tipo_operacao'], {
      is: exigeQualifCompleta,
      then: s => s.required('RG é obrigatório'),
      otherwise: s => s.nullable(),
    }),
  rg_orgao_emissor: yup
    .string()
    .nullable()
    .when(['tipo_pessoa', 'acao', 'tipos_alteracao_socio', '$tipo_operacao'], {
      is: exigeQualifCompleta,
      // UF so' onde o orgao e' exigido: e' onde o campo e' editavel (o socio que continua
      // tem o orgao do vigente em leitura e nao passa por aqui).
      then: s =>
        s
          .required('Órgão emissor do RG é obrigatório')
          .test('orgao-com-uf', MSG_ORGAO_COM_UF, v => !v || temOrgaoEUf(v))
          .max(ORGAO_COM_UF_MAX, MSG_ORGAO_TAMANHO),
      otherwise: s => s.nullable(),
    }),
  // Nacionalidade: default "brasileiro" via socioDefaults; EXPOSTA na UI
  // desde 2026-06-19 (StepSocios) — operador edita pra socio estrangeiro
  // (antes auto-brasileiro reverte decisao 2026-05-27, que gerava contrato
  // errado pra gringo). Adapter do backend mantem fallback "brasileiro" se
  // vier null/vazio. Nao e aspecto de alteracao (segue o vigente na alteracao).
  nacionalidade: yup.string().nullable(),
  estado_civil: yup
    .string()
    .nullable()
    .when(['tipo_pessoa', 'acao', 'tipos_alteracao_socio', '$tipo_operacao'], {
      is: exigeQualifCompleta,
      then: s =>
        s
          .oneOf(
            ESTADO_CIVIL_OPTIONS.map(o => o.value),
            'Estado civil inválido',
          )
          .required('Estado civil é obrigatório'),
      otherwise: s => s.nullable(),
    }),
  // Regime de casamento — obrigatorio quando estado_civil em
  // {casado, uniao_estavel}. Mesma regra do backend
  // (`_validar_socio_pf_regime`).
  regime_casamento: yup
    .string()
    .nullable()
    .when('estado_civil', {
      is: (estado: string | null | undefined) =>
        ESTADOS_QUE_EXIGEM_REGIME.includes(estado as EstadoCivil),
      then: s =>
        s
          .oneOf(
            REGIME_CASAMENTO_OPTIONS.map(o => o.value),
            'Regime inválido',
          )
          .required('Regime de casamento é obrigatório quando casado/união estável'),
      otherwise: s => s.nullable(),
    }),
  pacto_antenupcial: yup.boolean().nullable(),
  // Nome e CPF do cônjuge: obrigatórios (o CPF com o dígito conferido) só onde a regra vale;
  // fora dela seguem livres.
  nome_conjuge: yup
    .string()
    .nullable()
    .when(CHAVES_DO_CONJUGE, {
      is: cobraConjuge,
      then: s =>
        s
          .trim()
          .required(
            'Nome do cônjuge é obrigatório no regime de comunhão universal ou de separação obrigatória',
          ),
      otherwise: s => s.nullable(),
    }),
  cpf_conjuge: yup
    .string()
    .nullable()
    .when(CHAVES_DO_CONJUGE, {
      is: cobraConjuge,
      then: s =>
        s
          .required(
            'CPF do cônjuge é obrigatório no regime de comunhão universal ou de separação obrigatória',
          )
          .test(
            'cpf-conjuge-valido',
            'CPF do cônjuge inválido (dígito verificador não confere)',
            value => !value || validarCpf(value),
          )
          .test(
            'cpf-conjuge-de-outra-pessoa',
            'CPF do cônjuge não pode ser o do próprio sócio',
            function cpfDeOutraPessoa(value) {
              const doSocio = soDigitos(this.parent?.cpf);
              return !value || !doSocio || soDigitos(value) !== doSocio;
            },
          ),
      otherwise: s => s.nullable(),
    }),
  profissao: yup
    .string()
    .nullable()
    .when(['tipo_pessoa', 'acao', 'tipos_alteracao_socio', '$tipo_operacao'], {
      is: exigeQualifCompleta,
      then: s => s.required('Profissão é obrigatória'),
      otherwise: s => s.nullable(),
    }),
  // PJ obrigatorios — CNPJ valida DV (modulo 11) alem da presenca.
  cnpj: yup
    .string()
    .nullable()
    .when('tipo_pessoa', {
      is: 'pj',
      then: s =>
        s
          .required('CNPJ é obrigatório para Pessoa Jurídica')
          .test('cnpj-valido', 'CNPJ inválido (DV não confere)', value =>
            !value ? true : isValidCNPJ(value),
          ),
      otherwise: s => s.nullable(),
    }),
  // Campos FLAT do representante: required so pro PJ CONTINUANTE (acao!=adicionar),
  // que usa o caminho legacy v2. O PJ ADMITIDO (acao=adicionar) usa a sub-ficha
  // NESTED `representante.*` (validada na entrada `representante` abaixo) e NAO
  // renderiza os flats — exigi-los aqui bloqueava o PJ novo num campo que ele
  // nem preenche ("CPF do representante obrigatorio" mesmo com o nested completo).
  representante_nome: yup
    .string()
    .nullable()
    .when(['tipo_pessoa', 'acao', '$tipo_operacao'], {
      is: (tp: TipoPessoaSocio, acao: AcaoSocio | null, op: TipoOperacaoSocietario) =>
        tp === 'pj' && op === 'alteracao' && acao !== 'adicionar',
      then: s => s.required('Representante legal é obrigatório para Pessoa Jurídica'),
      otherwise: s => s.nullable(),
    }),
  representante_cpf: yup
    .string()
    .nullable()
    .when(['tipo_pessoa', 'acao', '$tipo_operacao'], {
      is: (tp: TipoPessoaSocio, acao: AcaoSocio | null, op: TipoOperacaoSocietario) =>
        tp === 'pj' && op === 'alteracao' && acao !== 'adicionar',
      then: s => s.required('CPF do representante é obrigatório para Pessoa Jurídica'),
      otherwise: s => s.nullable(),
    }),
  quotas: yup
    .number()
    .nullable()
    .when(['acao', 'tipo_vinculo'], {
      // Sessao 2026-05-11 — socio acao=sair nao precisa de quotas no form
      // (e o numero que ele tinha no vigente, nao no estado final).
      // tipo_vinculo='administrador' (nao-socio) nao tem quotas por definicao;
      // soh 'socio' e 'socio_administrador' precisam.
      is: (acao: AcaoSocio | null, tipoVinculo: TipoVinculoSocio) =>
        acao === 'sair' || tipoVinculo === 'administrador',
      then: s => s.nullable(),
      otherwise: s => s.min(1, 'Quotas deve ser maior que zero').required('Quotas é obrigatório'),
    }),
  // Range 1900-hoje — Pydantic v2 date rejeita ano fora de 1-9999 com 422.
  // Helper compartilhado com os demais campos de data do wizard.
  dt_nascimento: yup
    .string()
    .nullable()
    .test(
      dateRangeTest({
        min: '1900-01-01',
        max: 'today',
        name: 'dt-nascimento-range',
        message: 'Data de nascimento deve estar entre 1900-01-01 e hoje',
      }),
    )
    // Obrigatoria pra socio NOVO (adicionar) ou constituicao — o backend
    // exige (DadosPessoaisPF.data_nascimento) e sem isto falhava tarde com
    // 422 SOCIO_ADMITIDO_INCOMPLETO em vez de erro inline no Step 5. Tambem
    // pro continuante que marcou aspecto de qualif (regeneracao). Continuante
    // sem marcacao herda do vigente (Caminho B) — opcional.
    .when(['tipo_pessoa', 'acao', 'tipos_alteracao_socio', '$tipo_operacao'], {
      is: exigeQualifCompleta,
      then: s => s.required('Data de nascimento é obrigatória'),
      otherwise: s => s,
    }),
  // Sessao 2026-05-11 (socios sync). Espelha o backend Pydantic.
  acao: yup
    .mixed<AcaoSocio>()
    .oneOf(['manter', 'sair', 'alterar', 'adicionar'] as const)
    .nullable(),
  redistribuicao_quotas: yup
    .array()
    .nullable()
    .when(['acao', 'tipo_vinculo'], {
      // 2026-08-07: `tipo_vinculo='administrador'` (nao-socio) NAO detem quotas
      // por definicao — mesmo criterio ja usado no campo `quotas` acima. Ao sair,
      // ele nao tem o que redistribuir, e exigir >=1 destino travava a geracao
      // sem saida possivel: o destino pede `qtd >= 1`, mas o quadro ja fecha sem
      // ele, entao qualquer valor quebraria V_QUOTAS_TOTAL_BATE_VIGENTE_V3.
      // Caso real: troca de administrador (contrato 34.624.741/0001-65, alt. 6).
      is: (acao: AcaoSocio | null, tipoVinculo: TipoVinculoSocio) =>
        acao === 'sair' && tipoVinculo !== 'administrador',
      then: s =>
        s
          .of(
            yup.object().shape({
              socio_ordem: yup.number().nullable().min(0).required('Selecione um destinatário'),
              qtd: yup
                .number()
                .nullable()
                .min(1, 'Quotas deve ser positiva')
                .required('Informe a quantidade'),
            }),
          )
          .min(1, 'Quando sair, redistribua quotas para pelo menos 1 destino')
          // Sem mensagem, a lista nula caía no texto padrão do yup, em inglês e com o caminho.
          .required('Quando sair, redistribua quotas para pelo menos 1 destino'),
      otherwise: s => s.nullable(),
    }),
  // Sessao 2026-05-11 (item 15 Fase 4): sub-aspectos da alteracao. Espelha
  // o backend Pydantic — exige >=1 item quando acao=alterar; null caso contrario.
  tipos_alteracao_socio: yup
    .array()
    .nullable()
    .when('acao', {
      is: 'alterar',
      then: s =>
        s
          .of(
            yup
              .mixed<TipoAlteracaoSocio>()
              .oneOf([
                'quotas',
                'endereco',
                'nome',
                'profissao',
                'vinculo',
                'estado_civil',
                'representante',
              ] as const)
              .required(),
          )
          .min(1, 'Quando alterar, marque pelo menos 1 aspecto que muda')
          .required('Quando alterar, marque pelo menos 1 aspecto que muda'),
      otherwise: s => s.nullable(),
    }),

  // ============================================================
  // Schema-extension v3 elo 3 — campos v3 do socio.
  // socioSchema usa `$tipo_operacao` (context) pra acessar o
  // tipo_operacao do top-level (RHF passa via { context: { tipo_operacao } }
  // na resolucao). Como tipo_operacao nao esta no proprio socio,
  // usamos `yup.ref('$tipo_operacao')` pra ler o contexto raiz.
  //
  // Pragmatismo: `tipo_operacao` aqui e do top-level (wizard), nao do
  // socio. Yup nao suporta access ao top-level nativo num shape aninhado,
  // entao a regra "v3 exige genero PF" so dispara quando o validator do
  // socio rodar com o context populado. Em pratica, step1Schema/step2Schema
  // do wizard sao avaliados separados — se nao houver contexto, vale o
  // .nullable() padrao (validacao final acontece via gate backend no /gerar).
  // ============================================================
  // Pra constituicao v3, genero PF e obrigatorio (alimenta concordancia
  // gramatical no contrato). Em alteracao (v2), opcional (IA infere/usa
  // [A DEFINIR]). Cross-field com `$tipo_operacao` (passado via context
  // no `validateStep`).
  genero: yup
    .mixed<'M' | 'F'>()
    .oneOf(['M', 'F'] as const)
    .nullable()
    // Obrigatório pra novo/constituição E pro continuante que marcou aspecto
    // de qualif — o backend regenera a qualif e precisa do gênero pra
    // concordância ("brasileiro/brasileira"). Sem ele a regeneração falhava
    // silenciosamente (fail-open) e o endereço/aspecto não aplicava
    // (incidente 2026-06-11, contrato 2486).
    .when(['tipo_pessoa', 'acao', 'tipos_alteracao_socio', '$tipo_operacao'], {
      is: exigeQualifCompleta,
      then: s => s.required('Selecione o gênero do sócio'),
      otherwise: s => s.nullable(),
    }),
  rg_via: yup.string().nullable(),
  // junta_uf da PJ sócia (registro próprio). Obrigatória p/ PJ que precisa de
  // qualif completa (constituição OU admitida em alteração) — `DadosPessoaisPJ.
  // junta_uf` é required e, sem ela, a qualif do sócio sai VAZIA no preâmbulo da
  // consolidação (o derivador faz fail-open; incidente 4IC 2026-06-24).
  // Continuante PJ / PF: só valida o formato (nullable).
  junta_uf: yup
    .string()
    .nullable()
    .when(['tipo_pessoa', 'acao', '$tipo_operacao', '$natureza_societaria'], {
      // SCP: o sócio PJ pode não ter NIRE/Junta próprios (ZNEC não tem) — a SCP
      // relaxa; render condicional no backend. Só valida formato quando preenchido.
      is: (
        tp: TipoPessoaSocio,
        acao: AcaoSocio | null,
        op: TipoOperacaoSocietario,
        natureza: string | null,
      ) => tp === 'pj' && natureza !== 'scp' && (ehFluxoConstituinte(op) || acao === 'adicionar'),
      then: s =>
        s
          .matches(/^[A-Z]{2}$/, 'UF da Junta deve ter 2 letras')
          .required('UF da Junta da PJ é obrigatória'),
      otherwise: s =>
        s.when('tipo_pessoa', {
          is: 'pj',
          then: ss => ss.matches(/^[A-Z]{2}$/, 'UF deve ter 2 letras').nullable(),
          otherwise: ss => ss.nullable(),
        }),
    }),
  nire: yup.string().nullable(),
  // Em constituicao v3, sub-objeto e fonte unica de verdade do endereco
  // do socio (EnderecoForm escreve aqui). Campos criticos obrigatorios
  // pra que o gate backend `_gate_v3_prerequisitos` nao quebre o /gerar
  // com 422 silencioso — UX melhor falhar no step Revisão com mensagem
  // acionavel. Complemento fica opt.
  // Em alteracao, sub-objeto e opt (endereco legacy continua sendo a
  // fonte) — todos os campos nullable.
  endereco_estruturado: yup
    .object()
    .nullable()
    .when(['acao', 'tipos_alteracao_socio', '$tipo_operacao'], {
      is: (
        acao: AcaoSocio | null,
        tipos: string[] | null | undefined,
        tipoOp: TipoOperacaoSocietario,
      ) =>
        ehFluxoConstituinte(tipoOp) ||
        acao === 'adicionar' ||
        // Override 2026-06-11: continuante que marcou 'endereco' usa o
        // estruturado (o backend só reconstrói a qualif do estruturado).
        (acao === 'alterar' && Array.isArray(tipos) && tipos.includes('endereco')),
      then: s =>
        s
          .shape({
            logradouro: yup.string().nullable().required('Logradouro é obrigatório'),
            numero: yup.string().nullable().required('Número é obrigatório'),
            complemento: yup.string().nullable(),
            bairro: yup.string().nullable().required('Bairro é obrigatório'),
            cidade: yup.string().nullable().required('Cidade é obrigatória'),
            uf: yup
              .string()
              .nullable()
              .required('UF é obrigatória')
              .test('uf-2-letras', 'UF deve ter 2 letras', v => !v || /^[A-Z]{2}$/.test(v)),
            cep: yup
              .string()
              .nullable()
              .required('CEP é obrigatório')
              .test('cep-formato', 'CEP inválido (00000-000)', v => !v || /^\d{5}-?\d{3}$/.test(v)),
          })
          .required('Preencha o endereço estruturado do sócio'),
      otherwise: s =>
        s.shape({
          logradouro: yup.string().nullable(),
          numero: yup.string().nullable(),
          complemento: yup.string().nullable(),
          bairro: yup.string().nullable(),
          cidade: yup.string().nullable(),
          uf: yup
            .string()
            .nullable()
            .test('uf-2-letras', 'UF deve ter 2 letras', v => !v || /^[A-Z]{2}$/.test(v)),
          cep: yup
            .string()
            .nullable()
            .test('cep-formato', 'CEP inválido (00000-000)', v => !v || /^\d{5}-?\d{3}$/.test(v)),
        }),
    }),
  // Representante PJ — sub-ficha completa (v3 elo 3).
  // Em constituicao PJ: campos criticos required (mesmo conjunto que o
  // gate backend `_gate_v3_prerequisitos` exige).
  // Em ALTERACAO com PJ ADMITIDA (acao=adicionar): tambem required — o gate
  // `_construir_dados_pessoais_pj` monta o representante como DadosPessoaisPF
  // (RG, orgao, nascimento, profissao, estado civil, genero, endereco
  // residencial). O wizard so coletava nome+cpf flat -> 422
  // SOCIO_ADMITIDO_INCOMPLETO com mensagem "endereco (logradouro)..." que era a
  // do representante, nao da sede (incidente contrato 61, 2026-06-24).
  // Continuante PJ ou PF: opcional/nullable (usa o flat legacy v2).
  representante: yup
    .object()
    .nullable()
    .when(['tipo_pessoa', 'acao', '$tipo_operacao'], {
      is: (tp: TipoPessoaSocio, acao: AcaoSocio | null, op: TipoOperacaoSocietario) =>
        tp === 'pj' && (ehFluxoConstituinte(op) || acao === 'adicionar'),
      then: s =>
        s
          .shape({
            nome: yup.string().nullable().required('Nome do representante é obrigatório'),
            cpf: yup.string().nullable().required('CPF do representante é obrigatório'),
            rg: yup.string().nullable().required('RG do representante é obrigatório'),
            rg_orgao: yup
              .string()
              .nullable()
              .required('Órgão emissor do RG do representante é obrigatório')
              .test('orgao-com-uf', MSG_ORGAO_COM_UF, v => !v || temOrgaoEUf(v))
              .max(ORGAO_COM_UF_MAX, MSG_ORGAO_TAMANHO),
            rg_via: yup.string().nullable(),
            // Nacionalidade do representante: automatica via socioDefaults
            // (default 'brasileiro' cravado 2026-05-27).
            nacionalidade: yup.string().nullable(),
            estado_civil: yup
              .string()
              .nullable()
              .required('Estado civil do representante é obrigatório'),
            regime_casamento: yup
              .string()
              .nullable()
              .when('estado_civil', {
                is: (v: string | null) => ESTADOS_QUE_EXIGEM_REGIME.includes(v as EstadoCivil),
                then: r => r.required('Regime de bens é obrigatório quando casado / união estável'),
                otherwise: r => r.nullable(),
              }),
            dt_nascimento: yup
              .string()
              .nullable()
              .required('Data de nascimento do representante é obrigatória'),
            profissao: yup.string().nullable().required('Profissão do representante é obrigatória'),
            genero: yup
              .mixed<'M' | 'F'>()
              .oneOf(['M', 'F'] as const)
              .nullable()
              .required('Selecione o gênero do representante'),
            endereco_estruturado: yup
              .object()
              .nullable()
              .shape({
                logradouro: yup
                  .string()
                  .nullable()
                  .required('Logradouro do representante é obrigatório'),
                numero: yup
                  .string()
                  .nullable()
                  .required('Número do endereço do representante é obrigatório'),
                complemento: yup.string().nullable(),
                bairro: yup.string().nullable().required('Bairro do representante é obrigatório'),
                cidade: yup.string().nullable().required('Cidade do representante é obrigatória'),
                uf: yup
                  .string()
                  .nullable()
                  .required('UF do endereço do representante é obrigatória')
                  .test('uf-2-letras', 'UF deve ter 2 letras', v => !v || /^[A-Z]{2}$/.test(v)),
                cep: yup
                  .string()
                  .nullable()
                  .required('CEP do representante é obrigatório')
                  .test(
                    'cep-formato',
                    'CEP inválido (00000-000)',
                    v => !v || /^\d{5}-?\d{3}$/.test(v),
                  ),
              })
              .required('Endereço do representante é obrigatório'),
          })
          .required('Sub-ficha do representante é obrigatória em constituição v3'),
      otherwise: s =>
        s.shape({
          nome: yup.string().nullable(),
          cpf: yup.string().nullable(),
          rg: yup.string().nullable(),
          rg_orgao: yup.string().nullable(),
          rg_via: yup.string().nullable(),
          nacionalidade: yup.string().nullable(),
          estado_civil: yup.string().nullable(),
          regime_casamento: yup.string().nullable(),
          dt_nascimento: yup.string().nullable(),
          profissao: yup.string().nullable(),
          genero: yup
            .mixed<'M' | 'F'>()
            .oneOf(['M', 'F'] as const)
            .nullable(),
          endereco_estruturado: yup.object().nullable(),
        }),
    }),
});

/** Nome do teste da cessão no step2Schema: o toast mostra este erro antes dos demais. */
export const TESTE_DA_CESSAO = 'cessao-valida';

/** Problema da cessão: quem cede, a linha culpada e o campo dela que recebe o erro. */
export interface IProblemaDeCessao {
  indice: number;
  linha: number;
  campo: 'socio_ordem' | 'qtd';
  mensagem: string;
}

const emQuotas = (n: number): string => n.toLocaleString('pt-BR');

/**
 * Primeiro problema de cessão do quadro (review do PR #772): destino que sai do quadro, linha
 * incompleta de quem altera, ou cessão maior que o saldo de quem cede. Quem sai cede o vigente; quem altera, o vigente mais
 * o que recebe no mesmo ato (o back acha a ordem: do_formulario._em_ordem_executavel). Sem o
 * vigente (constituição, leitura sem quotas) só o destino é conferido. O erro vai no campo
 * que o operador corrige: o destino inválido, ou a quantidade da 1ª linha.
 */
export const problemaDeCessao = (
  socios: ISocioFormValues[],
  quotasVigentePorDoc: Record<string, number> | null | undefined,
): IProblemaDeCessao | null => {
  const recebidoPorOrdem = recebidoPorOrdemDe(socios);
  for (let indice = 0; indice < socios.length; indice += 1) {
    const s = socios[indice];
    if (s?.acao === 'sair' || s?.acao === 'alterar') {
      const cedente = s.nome || `Sócio ${indice + 1}`;
      const linhas = s.redistribuicao_quotas ?? [];
      const linha = linhas.findIndex(
        d => d?.socio_ordem != null && motivoDestinoInvalido(socios, d.socio_ordem) !== null,
      );
      const ordem = linhas[linha]?.socio_ordem;
      if (ordem != null) {
        const destino = socios[ordem];
        const quem = destino?.nome || `Sócio ${ordem + 1}`;
        const mensagem = destino
          ? `${cedente} cede quotas a ${quem}, que sai do quadro: escolha outro destino`
          : `${cedente} cede quotas a um sócio que não está mais no quadro: escolha outro destino`;
        return { indice, linha, campo: 'socio_ordem', mensagem };
      }
      // Quem altera: linha sem destinatário ou sem quantidade passava o Continuar e só voltava
      // no back (quem sai já tem essa regra no socioSchema).
      const incompleta =
        s.acao === 'alterar'
          ? linhas.findIndex(d => !!d && (d.socio_ordem == null || !(Number(d.qtd) > 0)))
          : -1;
      if (incompleta >= 0) {
        const semDestino = linhas[incompleta]?.socio_ordem == null;
        const mensagem = semDestino
          ? `${cedente} cede quotas sem destinatário: escolha quem recebe`
          : `${cedente} cede quotas sem informar quantas`;
        return { indice, linha: incompleta, campo: semDestino ? 'socio_ordem' : 'qtd', mensagem };
      }
      const vigente = parNoVigente(s, quotasVigentePorDoc);
      const recebido = s.acao === 'alterar' ? (recebidoPorOrdem[indice] ?? 0) : 0;
      const cedido = somaCedida(s);
      if (vigente != null && cedido > vigente + recebido) {
        let tem = emQuotas(vigente + recebido);
        if (recebido > 0) tem += ` (${emQuotas(vigente)} + ${emQuotas(recebido)} recebidas)`;
        const mensagem = `${cedente} cede ${emQuotas(cedido)} quotas, mas tem ${tem}`;
        return { indice, linha: 0, campo: 'qtd', mensagem };
      }
    }
  }
  return null;
};

export const step2Schema = yup.object().shape({
  socios: yup
    .array()
    .of(socioSchema)
    .min(1, 'Adicione ao menos um sócio')
    .test(
      'papeis-obrigatorios',
      'Papéis obrigatórios não atendidos',
      function validarPapeis(socios) {
        const lista = socios ?? [];
        // SCP: exige ≥1 Ostensiva + ≥1 Participante (eixo papel_scp).
        if (this.options.context?.natureza_societaria === 'scp') {
          const papel = (s: unknown) => (s as ISocioFormValues | undefined)?.papel_scp;
          // TODOS precisam de papel definido — um null vira sócio-fantasma no
          // back (some do preâmbulo/assinaturas mas fica na tabela de capital).
          if (lista.some(s => papel(s) !== 'ostensiva' && papel(s) !== 'participante')) {
            return this.createError({
              message: 'Defina o Papel (Ostensiva/Participante) de todos os sócios',
            });
          }
          if (!lista.some(s => papel(s) === 'ostensiva')) {
            return this.createError({ message: 'Marque ao menos 1 Sócia Ostensiva' });
          }
          if (!lista.some(s => papel(s) === 'participante')) {
            return this.createError({ message: 'Marque ao menos 1 Sócia Participante' });
          }
          return true;
        }
        // LTDA/Holding: ao menos 1 administrador.
        if (
          lista.some(
            s => s?.tipo_vinculo === 'socio_administrador' || s?.tipo_vinculo === 'administrador',
          )
        ) {
          return true;
        }
        return this.createError({
          message: 'Ao menos 1 sócio deve ser Sócio-administrador ou Administrador',
        });
      },
    )
    // "Dados de sócio" sem ninguém em "Alterar" com um aspecto de qualificação era um ato
    // vazio: o passo passava e o /gerar recusava (review do PR #772).
    .test(
      'dados-socio-com-alteracao',
      'Marque em algum sócio o que mudou (endereço, nome, profissão ou estado civil)',
      function exigirDadoDeSocio(socios) {
        const tipos = (this.parent?.tipos_alteracao ?? []) as string[];
        if (!tipos.includes('dados_socio')) return true;
        return (socios ?? []).some(s => {
          const socio = s as unknown as ISocioFormValues | undefined;
          return (
            socio?.acao === 'alterar' &&
            (socio.tipos_alteracao_socio ?? []).some(a => ASPECTOS_DE_QUALIFICACAO.includes(a))
          );
        });
      },
    )
    // Cessão para quem sai, ou maior que o saldo de quem cede, passava o Continuar e só
    // voltava no /gerar. O erro fica no campo da linha culpada (folha): o card do cedente
    // abre, e o erro some quando o operador corrige o campo.
    .test(TESTE_DA_CESSAO, 'Cessão de quotas inválida', function validarCessao(socios) {
      const problema = problemaDeCessao(
        (socios ?? []) as unknown as ISocioFormValues[],
        this.options.context?.quotasVigentePorDoc,
      );
      if (!problema) return true;
      const { indice, linha, campo, mensagem } = problema;
      return this.createError({
        path: `${this.path}[${indice}].redistribuicao_quotas[${linha}].${campo}`,
        message: mensagem,
      });
    })
    .required(),
});

export const step3Schema = yup.object().shape({
  razao_social: yup.string().nullable(),
  // CNPJ obrigatorio em alteracao (empresa ja registrada na Junta tem CNPJ).
  // Em constituicao fica opcional — pode nao ter sido emitido ainda. DV
  // sempre validado quando preenchido pra evitar input lixo na Junta.
  cnpj: yup
    .string()
    .nullable()
    .when('tipo_operacao', {
      is: 'alteracao',
      then: s =>
        s
          .required('CNPJ é obrigatório em alteração contratual')
          .test('cnpj-valido', 'CNPJ inválido (DV não confere)', value =>
            !value ? true : isValidCNPJ(value),
          ),
      otherwise: s =>
        s.test('cnpj-valido', 'CNPJ inválido (DV não confere)', value =>
          !value ? true : isValidCNPJ(value),
        ),
    }),
  capital_social: yup
    .number()
    .nullable()
    .min(0)
    // SCP: exige o capital total (evita "R$ 0,00" e habilita a checagem "capital
    // fecha"). O backend ainda deriva de qtd×valor por segurança.
    .when('natureza_societaria', {
      is: 'scp',
      then: s => s.required('Informe o capital social (total)').moreThan(0, 'Capital deve ser > 0'),
      otherwise: s => s,
    }),
  // Constituição: o objeto social é montado a partir dos CNAEs (cl. 3ª do v3),
  // então pelo menos 1 CNAE é obrigatório. Conta só CNAEs com conteúdo (o
  // CnaeSelector pode manter linhas-placeholder vazias).
  cnaes: yup.array().when(['tipo_operacao', 'natureza_societaria', 'dados_scp'], {
    // SCP: CNAE é exigido só no modo 'cnae' (livre usa objeto_social). LTDA/
    // Holding sempre exigem ≥1 CNAE. Espelha a condição do gate do backend.
    is: (op: TipoOperacaoSocietario, natureza: string | null, dscp: IDadosScp | null | undefined) =>
      ehFluxoConstituinte(op) && (natureza !== 'scp' || dscp?.objeto_modo === 'cnae'),
    then: s =>
      s.test(
        'cnae-min-1',
        'Selecione ao menos um CNAE — ele define o objeto social do contrato.',
        (arr: any[] | undefined) =>
          (arr ?? []).some((c: any) => c?.codigo?.trim() || c?.descricao?.trim()),
      ),
    otherwise: s => s,
  }),
  // Smoke 2026-05-12 Bug 1: validator backend V_OBJETO_SOCIAL_PRESENTE
  // (camada 3 DREI, non-overridable) rejeita objeto vazio. UI agora alinha:
  // - constituicao: NAO exigido (objeto vem dos CNAEs no v3 — sem campo manual).
  // - alteracao: obrigatorio quando o campo e editavel (`objeto_social` em
  //   tipos_alteracao OU `outras` marcado). Quando mantido do vigente, o
  //   form `objeto_social` vem do extrator IA — gate pre-IA so dispara o
  //   V_OBJETO_SOCIAL_PRESENTE se ate o extrator falhou (caso raro).
  objeto_social: yup
    .string()
    .nullable()
    .when(['tipo_operacao', 'tipos_alteracao', 'natureza_societaria', 'dados_scp'], {
      is: (
        tipoOp: TipoOperacaoSocietario,
        tipos: TipoAlteracao[] | undefined,
        natureza: string | null,
        dscp: IDadosScp | null | undefined,
      ) => {
        // SCP no modo 'livre': o objeto é TEXTO obrigatório (descrição do
        // projeto/empreendimento). No modo 'cnae' vem dos CNAEs.
        if (natureza === 'scp') return (dscp?.objeto_modo ?? 'livre') !== 'cnae';
        // LTDA/Holding constituição: objeto é DERIVADO dos CNAEs (cl. 3ª) — sem
        // campo manual. Alteração: exige quando editável.
        if (ehFluxoConstituinte(tipoOp)) return false;
        const t = tipos ?? [];
        return t.includes('objeto_social') || t.includes('outras');
      },
      then: s =>
        s
          .required('Informe o objeto social (descrição das atividades/empreendimento)')
          .min(30, 'Objeto muito curto — descreva em detalhe as atividades/empreendimento.'),
      otherwise: s => s.nullable(),
    }),
  endereco: yup.string().nullable(),
  // Foro — cláusula obrigatoria DREI (camada 3 v_foro_presente non-overridable).
  // Em alteracao (v2): valida `foro` string livre legacy.
  // Em constituicao (v3): valida `foro_cidade` + `foro_uf` estruturado abaixo,
  // legacy fica nullable (escondido na UI pra evitar duplicacao).
  foro: yup
    .string()
    .nullable()
    .when('tipo_operacao', {
      is: 'alteracao',
      then: s => s.required('Informe o foro / comarca de eleição'),
      otherwise: s => s.nullable(),
    }),
  finalidade_livre: yup.string().nullable(),
  // data_inicio_atividades pode ser futuro proximo (constituicao em curso).
  // hoje+30 e suspeito-mas-aceitavel. Mais que isso, operador esqueceu de
  // atualizar.
  data_inicio_atividades: yup
    .string()
    .nullable()
    .test(
      dateRangeTest({
        min: '1900-01-01',
        max: 'today+30',
        name: 'data-inicio-range',
        message: 'Data de início deve estar entre 1900 e até 30 dias no futuro',
      }),
    )
    // Constituição: a data vai na cl. 4ª (15/09) — sem ela o contrato cita o registro na Junta.
    .when(['tipo_operacao', 'natureza_societaria'], {
      is: (op: TipoOperacaoSocietario, natureza: string | null) =>
        op === 'constituicao' && natureza !== 'scp',
      then: s => s.required('Informe a data de início das atividades'),
      otherwise: s => s,
    }),
  // Cidade de assinatura aparece no fechamento do contrato — sem ela, IA
  // emite "[A DEFINIR]" no texto final.
  cidade_assinatura: yup.string().nullable().required('Informe a cidade de assinatura'),
  // Estado da junta — texto aceitando o NOME do UF (compat com o backend
  // que recebe 'Goias' e nao 'GO' nesse campo legacy). Bloqueia digitacao
  // livre via oneOf.
  junta_comercial_estado: yup
    .string()
    .nullable()
    .test('uf-valida', 'Selecione um estado válido', value =>
      !value ? true : UF_NOMES.includes(value),
    ),
  tipo_operacao: yup
    .mixed<TipoOperacaoSocietario>()
    .oneOf(['constituicao', 'alteracao', 'transformacao']),
  numero_alteracao: yup
    .number()
    .nullable()
    .when('tipo_operacao', {
      is: 'alteracao',
      then: s =>
        s
          .min(1, 'Informe o número da alteração (1, 2, 3...)')
          .required('Informe o número da alteração'),
      otherwise: s => s.nullable(),
    }),
  // Sigla da junta — formato depende do path:
  //
  // - Alteracao (v2): formato JUCXXX legacy (JUCESP, JUCEG, JUCERJA...).
  //   Regex 'JUC' + 2-5 letras cobre todas as siglas oficiais conhecidas.
  // - Constituicao (v3): UF de 2 letras (ex: 'SP', 'GO'). O adapter
  //   `_resolver_junta_uf` exige 2 letras pra resolver — sem isso o gate
  //   `_gate_v3_prerequisitos` quebra no /gerar. UfDropdown no StepEmpresa
  //   garante o formato.
  junta_comercial_sigla: yup
    .string()
    .nullable()
    .when('tipo_operacao', {
      is: 'alteracao',
      then: s =>
        s.test('sigla-junta', 'Sigla inválida (ex: JUCESP, JUCEG, JUCERJA)', value => {
          if (!value) return true;
          return /^JUC[A-Z]{2,5}$/.test(value.toUpperCase());
        }),
      // SCP (Fase 3) NÃO registra na Junta (art. 993) → sem UF da Junta.
      otherwise: s =>
        s.when('natureza_societaria', {
          is: 'scp',
          then: ss => ss.nullable(),
          otherwise: ss =>
            ss
              .required('Informe a UF da Junta Comercial')
              .test('uf-2-letras', 'UF deve ter 2 letras', v => !v || /^[A-Z]{2}$/.test(v)),
        }),
    }),
  // NIRE opcional — DREI nao exige mais como pre-requisito de alteracao.
  // Quando preenchido, valida formato 11/12 digitos. Se vazio, template
  // gera contrato sem o numero (operador edita manualmente se necessario).
  nire: yup
    .string()
    .nullable()
    .test('nire-formato', 'NIRE inválido — 11 ou 12 dígitos sem traço/ponto', value =>
      !value ? true : NIRE_REGEX.test(value),
    ),
  // Sem validacao (25/09): o campo nao aparece em nenhum fluxo, e uma data lida
  // fora do formato travava o "Continuar" sem campo para corrigir.
  data_constituicao: yup.string().nullable(),

  // ============================================================
  // Schema-extension v3 elo 3 — campos da constituicao v3.
  // Todos `.when('tipo_operacao', {is: 'constituicao', ...})` agrupados
  // aqui pra leitura linear. Alteracao continua com campos legacy.
  //
  // Backend (`IContratoSocietarioCreate` schema + adapter elo 4)
  // espera estes campos preenchidos pra rotear pra v3 — o gate
  // `_gate_v3_prerequisitos` do endpoint /gerar?engine=v3 (elo 5)
  // bloqueia geracao com 422 se algum faltar.
  // ============================================================
  foro_cidade: yup
    .string()
    .nullable()
    .when('tipo_operacao', {
      is: (op: TipoOperacaoSocietario) => ehFluxoConstituinte(op),
      then: s => s.required('Informe a cidade do foro (constituição v3)'),
      otherwise: s => s.nullable(),
    }),
  foro_uf: yup
    .string()
    .nullable()
    .when('tipo_operacao', {
      is: (op: TipoOperacaoSocietario) => ehFluxoConstituinte(op),
      then: s =>
        s
          .matches(/^[A-Z]{2}$/, 'UF deve ter 2 letras (ex: SP, GO)')
          .required('Selecione a UF do foro'),
      otherwise: s => s.nullable(),
    }),
  capital_qtd_quotas: yup
    .number()
    .nullable()
    .typeError('Quantidade de quotas deve ser número')
    // Limite espelha PG INTEGER (max 2.147.483.647) — backend retorna 422 amigável,
    // mas pegar no Yup evita um round-trip + mostra o erro inline no campo.
    .max(2_147_483_647, 'Quantidade de quotas excede o máximo permitido')
    .when('tipo_operacao', {
      is: (op: TipoOperacaoSocietario) => ehFluxoConstituinte(op),
      then: s =>
        s.min(1, 'Informe a quantidade de quotas').required('Quantidade de quotas é obrigatória'),
      otherwise: s => s.nullable(),
    }),
  // valor_quota: só pluri (socios.length >= 2). Uni assume R$ 1,00 fixo
  // — backend ignora o campo na uni. Yup permite null sempre; reset
  // reativo no StepEmpresa (useEffect) garante que vira null quando uni.
  capital_valor_quota: yup
    .number()
    .nullable()
    .typeError('Valor por quota deve ser número')
    .when(['tipo_operacao', 'socios'], {
      is: (tipoOp: TipoOperacaoSocietario, socios: ISocioFormValues[] | undefined) =>
        ehFluxoConstituinte(tipoOp) && (socios?.length ?? 0) >= 2,
      then: s =>
        s
          .min(0.01, 'Valor por quota deve ser > 0')
          .required('Valor por quota é obrigatório em constituição pluripessoal'),
      otherwise: s => s.nullable(),
    }),
  porte: yup
    .mixed<PorteEmpresarial>()
    // "Não informado" (option vazia do select) grava '': vira nulo antes do oneOf. Sem
    // isto a alteração recusava com "porte must be one of" e o Continuar travava.
    .transform(v => (v === '' ? null : v))
    .oneOf(['EPP', 'ME', 'nenhum'] as const)
    .nullable()
    // SCP não tem enquadramento de porte (EPP/ME) — não exigido.
    // Alteração: opcional (25/09). A leitura do vigente não traz o porte e o campo vazio
    // prendia o operador; sem ele o contrato sai sem visto do advogado e a vistoria avisa.
    .when(['tipo_operacao', 'natureza_societaria'], {
      is: (op: TipoOperacaoSocietario, natureza: string | null) =>
        ehFluxoConstituinte(op) && natureza !== 'scp',
      then: s => s.required('Selecione o porte empresarial'),
      otherwise: s => s.nullable(),
    }),
  natureza_societaria: yup
    .mixed<'ltda' | 'holding' | 'scp'>()
    .oneOf(['ltda', 'holding', 'scp'] as const)
    .nullable()
    .default('ltda'),
  // SCP: valida inline os sub-campos condicionais que o template renderiza
  // (espelha o `_gate_v3_scp` do back) — evita 422 tardio no /gerar. Como o
  // prazo 'determinado' é o default, a duração é o caso mais provável.
  dados_scp: yup
    .mixed()
    .test('scp-campos-condicionais', 'Campos da SCP obrigatórios', function validarDadosScp(dscp) {
      if (this.options.context?.natureza_societaria !== 'scp') return true;
      const d = (dscp ?? {}) as IDadosScp;
      if (
        (d.prazo_tipo ?? 'determinado') === 'determinado' &&
        (!d.prazo_duracao_valor || !d.prazo_duracao_unidade)
      ) {
        return this.createError({
          message: 'Informe a duração e a unidade do prazo (prazo determinado)',
        });
      }
      if (d.limite_alcada && !d.limite_alcada_valor) {
        return this.createError({ message: 'Informe o valor do limite de alçada' });
      }
      if (d.confidencialidade && !d.confidencialidade_multa) {
        return this.createError({ message: 'Informe a multa de confidencialidade' });
      }
      if (d.mediacao_previa && !d.mediacao_camara) {
        return this.createError({ message: 'Informe a câmara de mediação' });
      }
      return true;
    }),
  data_assinatura: yup
    .string()
    .nullable()
    .test(
      dateRangeTest({
        min: '1900-01-01',
        max: 'today+365',
        name: 'data-assinatura-range',
        message: 'Data de assinatura entre 1900 e até 1 ano no futuro',
      }),
    ),
  endereco_estruturado: yup
    .object()
    .nullable()
    .when('tipo_operacao', {
      is: (op: TipoOperacaoSocietario) => ehFluxoConstituinte(op),
      then: s => s.shape(camposDaSede(true)).required('Preencha o endereço da sede'),
    })
    // Alteração com "Endereço da sede" marcado: é a sede nova, nos mesmos campos.
    .when(['tipo_operacao', 'tipos_alteracao'], {
      is: (op: TipoOperacaoSocietario, tipos: TipoAlteracao[] | null | undefined) =>
        op === 'alteracao' && (tipos ?? []).includes('endereco'),
      then: s => s.shape(camposDaSede(false)).required('Preencha o endereço da sede'),
    }),
  // Filial aberta: o endereço é conferido no formulário dele e ao gerar; aqui, só os dados
  // facultativos (o capital destacado é um valor, e a soma cabe no capital).
  filiais: yup
    .array()
    .nullable()
    .when(['tipo_operacao', 'tipos_alteracao'], {
      is: (op: TipoOperacaoSocietario, tipos: TipoAlteracao[] | null | undefined) =>
        op === 'alteracao' && (tipos ?? []).includes('abertura_filial'),
      then: s =>
        s
          .of(yup.object().shape(dadosDaFilial()))
          .test('destaques-cabem', '', destaquesCabemNoCapital),
    }),
  filiais_fechar: yup
    .array()
    .nullable()
    .when(['tipo_operacao', 'tipos_alteracao'], {
      is: (op: TipoOperacaoSocietario, tipos: TipoAlteracao[] | null | undefined) =>
        op === 'alteracao' && (tipos ?? []).includes('extincao_filial'),
      then: s =>
        s
          .of(filialAFechar())
          .min(1, 'Informe a filial que será encerrada')
          // Engano comum: copiar o CNPJ da empresa. A cláusula sairia encerrando a "filial"
          // com o documento da matriz. Na lista, e não no item: é ela que enxerga o formulário.
          .test('cnpj-da-filial', '', function cnpjDaFilial(lista) {
            const limpo = (x: unknown) =>
              String(x ?? '')
                .replace(/[^A-Za-z0-9]/g, '')
                .toUpperCase();
            const daSociedade = limpo(this.parent?.cnpj);
            const i = (lista ?? []).findIndex(
              (f: any) => !!daSociedade && limpo(f?.cnpj) === daSociedade,
            );
            return i < 0
              ? true
              : this.createError({
                  path: `${this.path}[${i}].cnpj`,
                  message: 'Este é o CNPJ da própria sociedade: informe o da filial',
                });
          }),
    }),
  filiais_alterar: yup
    .array()
    .nullable()
    .when(['tipo_operacao', 'tipos_alteracao'], {
      is: (op: TipoOperacaoSocietario, tipos: TipoAlteracao[] | null | undefined) =>
        op === 'alteracao' && (tipos ?? []).includes('alteracao_filial'),
      then: s =>
        s
          .of(filialAAlterar())
          .min(1, 'Informe a filial que será alterada')
          .test('destaques-cabem', '', destaquesCabemNoCapital)
          // A mesma filial duas vezes, ou encerrada e mudando de endereço no mesmo ato.
          .test('filial-repetida', '', function filialRepetida(lista) {
            const limpo = (x: unknown) =>
              String(x ?? '')
                .replace(/[^A-Za-z0-9]/g, '')
                .toUpperCase();
            const encerra = (this.parent?.tipos_alteracao ?? []).includes('extincao_filial');
            const encerradas = encerra
              ? (this.parent?.filiais_fechar ?? []).map((f: any) => limpo(f?.cnpj))
              : [];
            const cnpjs = (lista ?? []).map((f: any) => limpo(f?.cnpj));
            const i = cnpjs.findIndex(
              (c, k) => !!c && (cnpjs.indexOf(c) !== k || encerradas.includes(c)),
            );
            return i < 0
              ? true
              : this.createError({
                  path: `${this.path}[${i}].cnpj`,
                  message: encerradas.includes(cnpjs[i])
                    ? 'Esta filial está sendo encerrada neste ato'
                    : 'Esta filial já está na lista (CNPJ repetido)',
                });
          })
          // O mesmo engano da extinção: o CNPJ da empresa no lugar do da filial.
          .test('cnpj-da-filial', '', function cnpjDaFilial(lista) {
            const limpo = (x: unknown) =>
              String(x ?? '')
                .replace(/[^A-Za-z0-9]/g, '')
                .toUpperCase();
            const daSociedade = limpo(this.parent?.cnpj);
            const i = (lista ?? []).findIndex(
              (f: any) => !!daSociedade && limpo(f?.cnpj) === daSociedade,
            );
            return i < 0
              ? true
              : this.createError({
                  path: `${this.path}[${i}].cnpj`,
                  message: 'Este é o CNPJ da própria sociedade: informe o da filial',
                });
          }),
    }),
});

// Step 4 — validacao ao clicar "Gerar contrato".
// Em alteracao, o texto livre e obrigatorio — sem isso a IA nao tem base
// pra compor ementa/clausulas e acaba retornando entries com [A DEFINIR].
export const step4Schema = yup.object().shape({
  tipo_operacao: yup
    .mixed<TipoOperacaoSocietario>()
    .oneOf(['constituicao', 'alteracao', 'transformacao']),
  // finalidade_livre (instrução para a IA) FOI REMOVIDA do wizard (2026-07-15):
  // o v3 é determinístico e a ignora. Deixa de ser obrigatória na alteração —
  // senão o step travaria com o campo sempre vazio.
  finalidade_livre: yup.string().nullable(),
});
